import React from 'react';
import { useTranslation } from 'react-i18next';

import Item from 'component/Accommodation/Recommender/Item';
import Slider from 'component/Layout/Slider';

import classNames from 'classnames';

import { QaIdentifier, useQa } from 'hooks/useQa';

import Incentive from '../Item/Incentive';

import { ListProps } from './types';

import styles from './List.module.scss';

const List = ({
    hotelName,
    hotelItems,
    trackMethod,
    renderHeadline,
    headline,
    onMoreClick,
    circledNavigation = false,
    swiperSettings,
    classListObject,
    withRating = true,
    showLocation = false,
    navigationOnSlider = false,
    customLinkTarget,
    withHeaderLink = false,
}: ListProps) => {
    const { t } = useTranslation();
    const qaAttr = useQa(QaIdentifier.Accommodation_Recommender);

    const settings = {
        pagination: false,
        spaceBetween: 15,
        slidesPerView: 2,
        ...swiperSettings,
    };

    const items = hotelItems.map((hotelItem, index) => {
        const { masterData } = hotelItem;

        return (
            <Item
                key={hotelItem.masterData?.id}
                masterData={{
                    id: masterData?.id,
                    hotelName: masterData?.hotelName,
                    hotelCategory: masterData?.hotelCategory,
                    hotelCategoryType: masterData?.hotelCategoryType,
                    image: masterData?.image,
                    destinationLabel: masterData?.destinationLabel,
                    rating: masterData?.rating,
                    isHolidayHome: masterData?.isHolidayHome ?? false,
                }}
                index={index}
                trackMethod={trackMethod}
                customLinkTarget={customLinkTarget}
                withRating={withRating}
                classListObject={{
                    CardWrapper: classListObject?.CardWrapper,
                    Thumbnail: classNames(
                        classListObject?.Thumbnail,
                        styles.Thumbnail
                    ),
                    Hotel: classListObject?.Hotel,
                    TitleWrapper: classListObject?.TitleWrapper,
                    Title: classListObject?.Title,
                    Loyalty: classListObject?.Loyalty,
                    Price: classNames(classListObject?.Price, styles.Price),
                    Category: classListObject?.Category,
                    Accommodation: classNames(
                        classListObject?.AccommodationRating,
                        styles.AccommodationRating
                    ),
                }}
                tourOperator={{
                    showLogo:
                        'showTourOperatorLogo' in hotelItem
                            ? !!hotelItem?.showTourOperatorLogo
                            : false,
                    image:
                        'tourOperator' in hotelItem
                            ? hotelItem.tourOperator?.image?.url
                            : undefined,
                    code:
                        'tourOperator' in hotelItem
                            ? hotelItem.tourOperator?.code
                            : undefined,
                }}
                points={'points' in hotelItem ? hotelItem.points : undefined}
                showLocation={showLocation}
                meta={{
                    days: 'days' in hotelItem ? hotelItem.days : undefined,
                    traveller: hotelItem.traveller,
                    price: hotelItem.price,
                    url: hotelItem.url,
                    strikePrice:
                        'strikePrice' in hotelItem
                            ? hotelItem.strikePrice
                            : undefined,
                }}
                isSponsored={
                    'isSponsored' in hotelItem ? hotelItem.isSponsored : false
                }
                withHeaderLink={withHeaderLink}
            />
        );
    });
    onMoreClick &&
        items.push(
            <Incentive key={hotelItems.length + 1} onMoreClick={onMoreClick} />
        );

    return (
        <section className={styles.List} {...qaAttr}>
            {renderHeadline && (
                <h2
                    className={styles.ListHeadline}
                    dangerouslySetInnerHTML={{
                        __html:
                            headline ??
                            t('TRANS-recommender-headline', {
                                hotelName,
                            }),
                    }}
                />
            )}
            {hotelItems && hotelItems.length > 3 ? (
                <Slider
                    settings={{
                        ...settings,
                    }}
                    classListObject={{
                        Slide: classNames(classListObject?.Slide),
                        SliderWrapper: classNames(
                            styles.Slider,
                            classListObject?.SliderWrapper
                        ),
                        PrevButton: classNames(
                            styles.PrevButton,
                            { [styles.Default]: !circledNavigation },
                            classListObject?.PrevButton
                        ),
                        NextButton: classNames(
                            styles.NextButton,
                            { [styles.Default]: !circledNavigation },
                            classListObject?.NextButton
                        ),
                        DisabledButton: styles.DisabledButton,
                    }}
                    circledNavigation={circledNavigation}
                    navigationOnSlider={navigationOnSlider}
                >
                    {items}
                </Slider>
            ) : (
                <div className={styles.TwofoldRecommender}>{items}</div>
            )}
        </section>
    );
};
export default List;
